// Main chat interface component

import React, {useState, useRef, useEffect, useCallback} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {useChat} from '../hooks/useChat';
import MessageBubble from './MessageBubble';
import { chatApi } from '../services/chatApi';
import { ConversationSidebar } from './ConversationSidebar';
import { authService } from '../services/authService';
import { ROUTES } from '../constants/routes';
import Toast from './Toast';
import { parseToolCallMessage } from '../utils/conversationExport';

// Delay to ensure images and heavy content start loading before scrolling
// Adjust if needed based on typical content load times
const SCROLL_DELAY_MS = 150;

// Threshold for determining if user is "near bottom" of chat
const SCROLL_THRESHOLD_PX = 100;

const ChatInterface: React.FC = () => {
    const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [isNewChatTransition, setIsNewChatTransition] = useState(false);
    const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [messageLimit, setMessageLimit] = useState<number>(20); // Default to 20, will be fetched from backend
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const shouldAutoScrollRef = useRef(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const loadedConversationIdRef = useRef<string | undefined>(undefined);
    const processedQueryRef = useRef<string | null>(null);
    const wasStreamingRef = useRef(false);

    const {
        messages,
        isStreaming,
        currentStreamingMessage,
        error,
        conversationId,
        messageFeedback,
        sendMessage,
        stopStreaming,
        clearMessages,
        setConversationId
    } = useChat(urlConversationId);

    // Fetch chat configuration on mount
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const config = await chatApi.getConfig();
                setMessageLimit(config.messageLimit);
            } catch (error) {
                console.error('Failed to fetch chat config:', error);
                // Keep default value of 20 if fetch fails
            }
        };

        fetchConfig();
    }, []);

    // Update URL when conversation ID changes
    useEffect(() => {
        // RACE CONDITION FIX: Add a small delay to let React Router update first
        const timeoutId = setTimeout(() => {
            if (conversationId && conversationId !== urlConversationId) {
                // Mark conversation as loaded when we create it (to prevent reload from backend)
                loadedConversationIdRef.current = conversationId;
                // Clear the new chat transition flag when a conversation is created
                setIsNewChatTransition(false);
                // Navigate to conversation URL if we have a conversation ID and we're not already there
                navigate(ROUTES.getConversationRoute(conversationId), { replace: true });
            } else if (!conversationId && urlConversationId) {
                // Clear URL if no conversation but URL has one
                navigate(ROUTES.NEW_CONVERSATION, { replace: true });
            }
        }, 10); // Small delay to let React Router process

        return () => clearTimeout(timeoutId);
    }, [conversationId, urlConversationId, navigate]);

    // Load conversation from URL when it changes
    useEffect(() => {
        if (urlConversationId && !isNewChatTransition) {
            // Only load conversation if we haven't loaded this ID yet
            // This prevents reloading messages we just sent AND allows initial load from URL
            if (urlConversationId !== loadedConversationIdRef.current) {
                loadedConversationIdRef.current = urlConversationId;
                setConversationId(urlConversationId).catch((error) => {
                    console.error('Failed to load conversation from URL:', error);
                    loadedConversationIdRef.current = undefined; // Reset on error
                    // Redirect to new conversation if conversation doesn't exist or can't be loaded
                    navigate(ROUTES.NEW_CONVERSATION, { replace: true });
                });
            }
        } else if (!urlConversationId) {
            // We're on /conversations/new - clear the transition flag and loaded ID
            setIsNewChatTransition(false);
            loadedConversationIdRef.current = undefined;
        }
    }, [urlConversationId, isNewChatTransition, setConversationId, navigate]);

    // Feedback loading is now handled in useChat.ts setConversationId()
    // This ensures feedback is only loaded when loading EXISTING conversations,
    // not when creating NEW conversations (which was causing race conditions)

    // Trigger sidebar refresh when streaming completes
    useEffect(() => {
        // Detect when streaming stops (was true, now false)
        if (wasStreamingRef.current && !isStreaming && conversationId) {
            // Increment trigger to refresh sidebar
            setSidebarRefreshTrigger(prev => prev + 1);
        }
        wasStreamingRef.current = isStreaming;
    }, [isStreaming, conversationId]);

    // Handle query parameter for auto-submitting questions (e.g., from entity suggestions)
    useEffect(() => {
        const queryParam = searchParams.get('q');

        // Clear processed query ref when there's no query parameter
        if (!queryParam && processedQueryRef.current) {
            processedQueryRef.current = null;
            return;
        }

        // Only submit if:
        // 1. There's a query parameter
        // 2. Not currently streaming
        // 3. Haven't processed this query yet
        // 4. Either no conversation ID (new conversation) OR conversation is loaded (conversationId matches AND messages are loaded)
        const isExistingConversationReady = urlConversationId && conversationId === urlConversationId && messages.length > 0;
        const isNewConversationReady = !urlConversationId;
        const isConversationReady = isNewConversationReady || isExistingConversationReady;

        if (queryParam && !isStreaming && queryParam !== processedQueryRef.current && isConversationReady) {
            // Mark this query as processed to prevent double submission
            processedQueryRef.current = queryParam;

            // Clear the query parameter from URL
            setSearchParams({});

            // Submit the message
            sendMessage(queryParam).then(() => {
                // Clear input after successful send
                setInputValue('');
            });
        }
    }, [searchParams, isStreaming, sendMessage, setSearchParams, urlConversationId, conversationId, messages.length]);

    const handleNewConversation = useCallback(() => {
        // Stop any ongoing smooth scroll animations immediately
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({behavior: 'auto'});
        }

        // Set flag to prevent conversation loading during transition
        setIsNewChatTransition(true);

        // Reset loaded conversation tracking
        loadedConversationIdRef.current = undefined;
        processedQueryRef.current = null; // Reset query tracking for new conversation

        // Clear everything including conversationId to prevent infinite loop
        clearMessages();

        // Navigate to /conversations/new
        navigate(ROUTES.NEW_CONVERSATION);
    }, [clearMessages, navigate]);

    // Handle scroll events to determine if user has scrolled up
    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (container) {
            const isNearBottom =
                container.scrollHeight - container.scrollTop - container.clientHeight < SCROLL_THRESHOLD_PX;
            shouldAutoScrollRef.current = isNearBottom;
        }
    }, []);

    // Auto-scroll to bottom when new messages arrive (with smart scrolling)
    useEffect(() => {
        // Clean up any existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Only auto-scroll if user is near the bottom or hasn't scrolled up
        if (shouldAutoScrollRef.current) {
            const scrollToBottom = () => {
                // Multiple requestAnimationFrame calls to ensure DOM is fully rendered
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        // Add a timeout to ensure images and content are loaded
                        scrollTimeoutRef.current = setTimeout(() => {
                            if (messagesEndRef.current && shouldAutoScrollRef.current) {
                                messagesEndRef.current.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'end'
                                });
                            }
                        }, SCROLL_DELAY_MS);
                    });
                });
            };

            scrollToBottom();
        }

        // Cleanup timeout on unmount or when dependencies change
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, [messages, currentStreamingMessage]);

    // Auto-resize textarea
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [inputValue]);

    // Auto-focus textarea on mount
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.focus();
        }
    }, []);

    // Load suggestions on mount
    useEffect(() => {
        const loadSuggestions = async () => {
            try {
                const data = await chatApi.getSuggestions();
                setSuggestions(data);
            } catch (error) {
                console.warn('Failed to load suggestions:', error);
                // This should not happen as chatApi.getSuggestions() has internal fallback
                setSuggestions([
                    "What can you tell me about LEGO set 76386?",
                    "Show me Harry Potter themed sets",
                    "Find sets with trans-clear parts",
                    "What are the largest LEGO sets?",
                    "Tell me about Creator Expert sets"
                ]);
            }
        };

        loadSuggestions();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || isStreaming) return;

        await sendMessage(inputValue);
        setInputValue('');
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handleSuggestionClick = useCallback(async (suggestion: string) => {
        if (isStreaming) return;
        // Enable auto-scroll when clicking suggestions to ensure we scroll to the new message
        shouldAutoScrollRef.current = true;
        await sendMessage(suggestion);
    }, [isStreaming, sendMessage]);

    const handleReloadConversation = useCallback(async () => {
        if (!conversationId) return;
        try {
            await setConversationId(conversationId);
        } catch (error) {
            console.error('Failed to reload conversation:', error);
        }
    }, [conversationId, setConversationId]);

    // Handle URL hash for suggestions (e.g., #/suggestion/How%20much%20does...)
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;
            if (hash.startsWith('#/suggestion/')) {
                try {
                    const suggestionText = decodeURIComponent(hash.replace('#/suggestion/', ''));
                    if (suggestionText) {
                        handleSuggestionClick(suggestionText);
                        // Clear the hash without adding to history
                        history.replaceState(null, '', window.location.pathname + window.location.search);
                    }
                } catch (error) {
                    console.error('Failed to decode suggestion URL:', error);
                }
            }
        };

        // Handle initial hash on mount
        handleHashChange();

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [handleSuggestionClick]);

    // Generate markdown content for conversation export
    const generateConversationMarkdown = useCallback((): string => {
        // Generate Markdown content
        let markdown = `# Conversation Export\n\n`;
        markdown += `**Conversation ID:** ${conversationId}\n\n`;
        markdown += `**Exported:** ${new Date().toISOString()}\n\n`;
        markdown += `**Messages:** ${messages.length}\n\n`;
        markdown += `---\n\n`;

        messages.forEach((msg, index) => {
            // Parse and format tool call messages
            const toolCallInfo = parseToolCallMessage(msg.content);

            if (toolCallInfo.isToolCall) {
                if (toolCallInfo.type === 'input') {
                    // Add tool name as h2 with down arrow prefix (input)
                    markdown += `## ⬇️ ${toolCallInfo.toolName}\n\n`;

                    // Format input parameters as code block with json syntax highlighting
                    if (toolCallInfo.body) {
                        markdown += `\`\`\`json\n${toolCallInfo.body}\n\`\`\`\n\n`;
                    }
                } else if (toolCallInfo.type === 'output') {
                    // Add tool name as h2 with up arrow prefix (output)
                    markdown += `## ⬆️ ${toolCallInfo.toolName}\n\n`;

                    // Format output as blockquote
                    if (toolCallInfo.body) {
                        // Try to parse as JSON string first for better unquoting
                        let cleanBody = toolCallInfo.body.trim();
                        try {
                            // If body is a JSON string, parse it to remove quotes properly
                            if (cleanBody.startsWith('"') && cleanBody.endsWith('"')) {
                                cleanBody = JSON.parse(cleanBody);
                            }
                        } catch {
                            // If JSON.parse fails, fall back to simple quote removal
                            if (cleanBody.startsWith('"') && cleanBody.endsWith('"')) {
                                cleanBody = cleanBody.slice(1, -1);
                            }
                        }

                        // Handle both literal \n and actual newlines
                        const normalizedBody = cleanBody.replace(/\\n/g, '\n');
                        const lines = normalizedBody.split(/\r?\n/);
                        const blockquote = lines.map(line => `> ${line}`).join('\n');
                        markdown += `${blockquote}\n\n`;
                    }
                }
            } else {
                // Message header for non-tool-call messages
                if (msg.type === 'user') {
                    markdown += `## 👤 User\n\n`;
                } else if (msg.type === 'assistant') {
                    markdown += `## 🤖 Assistant\n\n`;
                } else {
                    markdown += `## ${msg.type.toUpperCase()}\n\n`;
                }

                // Regular message content
                markdown += `${msg.content}\n\n`;
            }

            // Suggestions (if any)
            if (msg.suggestions && msg.suggestions.length > 0) {
                markdown += `## 💡 Suggestions\n\n`;
                msg.suggestions.forEach(suggestion => {
                    markdown += `- ${suggestion}\n`;
                });
                markdown += `\n`;
            }

            // Timestamp
            markdown += `*${msg.timestamp.toLocaleString()}*\n\n`;

            // Separator between messages
            if (index < messages.length - 1) {
                markdown += `---\n\n`;
            }
        });

        return markdown;
    }, [conversationId, messages]);

    // Copy conversation to clipboard for admin users
    const handleCopyConversation = useCallback(async () => {
        if (!conversationId || messages.length === 0) return;

        // Warn for large conversations
        if (messages.length > 100) {
            const confirmed = window.confirm(
                `This conversation has ${messages.length} messages. Large exports may be slow or fail. Continue?`
            );
            if (!confirmed) return;
        }

        const markdown = generateConversationMarkdown();

        // Copy to clipboard
        try {
            await navigator.clipboard.writeText(markdown);
            setToast({ message: 'Conversation copied to clipboard', type: 'success' });
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
            // Fallback: select and copy
            const textArea = document.createElement('textarea');
            textArea.value = markdown;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setToast({ message: 'Conversation copied to clipboard', type: 'success' });
            } catch (fallbackErr) {
                console.error('Fallback copy failed:', fallbackErr);
                setToast({ message: 'Failed to copy to clipboard', type: 'error' });
            }
            document.body.removeChild(textArea);
        }
    }, [conversationId, messages, generateConversationMarkdown]);

    // Download conversation as markdown file
    const handleDownloadConversation = useCallback(() => {
        if (!conversationId || messages.length === 0) return;

        // Warn for large conversations
        if (messages.length > 100) {
            const confirmed = window.confirm(
                `This conversation has ${messages.length} messages. Large exports may be slow or fail. Continue?`
            );
            if (!confirmed) return;
        }

        try {
            const markdown = generateConversationMarkdown();

            // Create a Blob with the markdown content
            const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });

            // Create a download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Generate filename with conversation ID and timestamp
            const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
            link.download = `conversation-${conversationId}-${timestamp}.md`;

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setToast({ message: 'Conversation downloaded successfully', type: 'success' });
        } catch (err) {
            console.error('Failed to download conversation:', err);
            setToast({ message: 'Failed to download conversation', type: 'error' });
        }
    }, [conversationId, messages, generateConversationMarkdown]);

    const isAuthenticated = authService.isAuthenticated();

    // Check if the error is a message limit error
    const isMessageLimitError = error && error.includes('Message limit reached');

    // Check if we've reached the message limit based on current message count
    // Only count persisted messages (user and assistant) since tool messages are not persisted
    // We disable input when we have (messageLimit - 2) or more messages because adding another
    // user message + assistant response (2 messages) would exceed the limit
    // This aligns with the backend's validation logic: (currentMessageCount + 2) > messageLimit
    const persistedMessageCount = messages.filter(msg => msg.type === 'user' || msg.type === 'assistant').length;
    const hasReachedMessageLimit = persistedMessageCount >= (messageLimit - 2);

    return (
        <div className="chat-layout">
            {/* Conversation Sidebar - only show if authenticated */}
            {isAuthenticated && (
                <ConversationSidebar
                    currentConversationId={conversationId}
                    onNewConversation={handleNewConversation}
                    refreshTrigger={sidebarRefreshTrigger}
                />
            )}

            {/* Main Chat Container */}
            <div className="chat-container">

            {/* Copy Conversation Button */}
            {messages.length > 0 && (
                <button
                    onClick={handleCopyConversation}
                    className="admin-copy-btn"
                    title="Copy conversation to clipboard"
                    type="button"
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                </button>
            )}

            {/* Download Conversation Button */}
            {messages.length > 0 && (
                <button
                    onClick={handleDownloadConversation}
                    className="admin-download-btn"
                    title="Download conversation as markdown"
                    type="button"
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                    </svg>
                </button>
            )}

            {/* Messages Area */}
            <div
                className="chat-messages"
                ref={messagesContainerRef}
                onScroll={handleScroll}
            >
                {messages.length === 0 && !isStreaming && (
                    <div className="welcome-section text-center">
                        <h3 className="welcome-title">
                            Welcome! Try asking about LEGO sets:
                        </h3>
                        <div className="suggestions-container">
                            {suggestions.slice(0, 3).map((suggestion, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    className="suggestion-button"
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((message) => (
                    <MessageBubble
                        key={message.id}
                        message={message}
                        isStreaming={isStreaming && currentStreamingMessage?.id === message.id}
                        onSuggestionClick={handleSuggestionClick}
                        conversationId={conversationId}
                        existingFeedback={messageFeedback[message.id]}
                        onReloadConversation={handleReloadConversation}
                    />
                ))}

                {/* Show suggestions after the last completed assistant message */}
                {!isStreaming && !isMessageLimitError && !hasReachedMessageLimit && (() => {
                    // Find the last assistant message with suggestions
                    const lastMessageWithSuggestions = [...messages]
                        .reverse()
                        .find(msg =>
                            msg.type === 'assistant' &&
                            msg.isComplete &&
                            msg.suggestions &&
                            msg.suggestions.length > 0
                        );

                    if (lastMessageWithSuggestions?.suggestions) {
                        return (
                            <div className="message-suggestions">
                                {lastMessageWithSuggestions.suggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="suggestion-button"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        );
                    }
                    return null;
                })()}

                {/* Show message limit warning when limit is reached */}
                {hasReachedMessageLimit && !isMessageLimitError && (
                    <div className="message message-error">
                        ⚠️ Message limit reached. This conversation has reached the maximum of {messageLimit} messages. Please start a new conversation to continue.
                    </div>
                )}

                {error && !isMessageLimitError && (
                    <div className="message message-error">
                        <strong>Error:</strong> {error}
                    </div>
                )}

                <div ref={messagesEndRef}/>
            </div>

            {/* Input Area */}
            <div className="chat-input-container">
                <form onSubmit={handleSubmit} className="chat-input-form">
                    {/* New Chat icon button - positioned inside text input */}
                    <button
                        onClick={handleNewConversation}
                        disabled={isStreaming || messages.length === 0}
                        type="button"
                        className="mobile-new-chat-btn"
                        title="New Chat"
                    >
                        <svg className="mobile-icon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                        </svg>
                    </button>

                    <textarea
                        ref={textareaRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={hasReachedMessageLimit || isMessageLimitError ? "Message limit reached - start a new conversation" : "Ask me about LEGO sets, parts..."}
                        className="chat-input"
                        rows={1}
                        disabled={isStreaming || isMessageLimitError || hasReachedMessageLimit}
                    />

                    {/* Send/Stop button - positioned inside text input */}
                    <button
                        type={isStreaming ? "button" : "submit"}
                        disabled={isMessageLimitError || hasReachedMessageLimit || (!isStreaming && !inputValue.trim())}
                        onClick={isStreaming ? stopStreaming : undefined}
                        className={`mobile-send-btn ${isStreaming ? 'stop-mode' : ''}`}
                        title={isStreaming ? "Stop generation" : (isMessageLimitError || hasReachedMessageLimit) ? "Message limit reached" : "Send message"}
                    >
                        {isStreaming ? (
                            // Stop icon - square shape
                            <svg className="mobile-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 6h12v12H6z" />
                            </svg>
                        ) : (
                            // Send icon - arrow shape
                            <svg className="mobile-icon" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                        )}
                    </button>

                </form>
                {/* AI Disclaimer */}
                <div className="chat-disclaimer">
                    AI can make mistakes. Please verify important information.
                </div>
            </div>
            </div>

            {/* Toast notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default ChatInterface;
